import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'

/**
 * Combobox — a text input that filters a list of options as you type.
 *
 * Props:
 *   options      [{ value, label }]   List of selectable options
 *   value        any                  Currently selected value (the value field, not label)
 *   onChange     (value) => void      Called with the selected value when an option is chosen
 *   placeholder  string               Input placeholder text
 *   disabled     bool
 *   className    string               Extra class on the wrapper div (use for sizing)
 *
 * Keyboard behavior:
 *   ArrowDown / ArrowUp   Navigate options
 *   Enter                 Select highlighted option (or sole remaining option)
 *   Escape                Close and restore previous selection
 */
export const Combobox = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}) => {
  // inputText is only used while the dropdown is open (for filtering).
  // When closed, displayValue falls back to selectedLabel directly — no sync needed.
  const [inputText, setInputText] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef(null)

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || ''

  // When open: show what the user is typing. When closed: always show the selected label.
  const displayValue = isOpen ? inputText : selectedLabel

  const filtered = isOpen && inputText
    ? options.filter(o => o.label.toLowerCase().includes(inputText.toLowerCase()))
    : options

  const handleInputChange = e => {
    setInputText(e.target.value)
    setActiveIndex(-1)
  }

  const handleFocus = () => {
    // Seed the filter text with the current label so typing replaces it naturally.
    setInputText(selectedLabel)
    setIsOpen(true)
    setActiveIndex(-1)
    // Select all text so the user can start typing immediately.
    requestAnimationFrame(() => inputRef.current?.select())
  }

  const handleBlur = () => {
    setIsOpen(false)
    // displayValue auto-snaps back to selectedLabel — nothing else needed.
  }

  const handleSelect = option => {
    onChange(option.value)
    setIsOpen(false)
  }

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current.blur()
      return
    }
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setInputText(selectedLabel)
        setIsOpen(true)
        setActiveIndex(0)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target =
        activeIndex >= 0 && filtered[activeIndex]
          ? filtered[activeIndex]
          : filtered.length === 1
          ? filtered[0]
          : null
      if (target) handleSelect(target)
    }
  }

  return (
    <div style={{ position: 'relative' }} className={className}>
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-sm"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-haspopup="listbox"
      />
      {isOpen && (
        <ul
          role="listbox"
          className="combobox-dropdown"
          onMouseDown={e => e.preventDefault()}
        >
          {filtered.length === 0 ? (
            <li className="combobox-no-results">No results</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={String(opt.value) === String(value)}
                className={[
                  'combobox-option',
                  i === activeIndex ? 'combobox-option-active' : '',
                  String(opt.value) === String(value) ? 'combobox-option-selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

Combobox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

Combobox.defaultProps = {
  placeholder: 'Select…',
  disabled: false,
}
